import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'fo-theme-mode';

function systemPrefersDark() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolve(mode: ThemeMode): ResolvedTheme {
  return mode === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : mode;
}

function readStoredMode(): ThemeMode {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
}

interface ThemeContextValue {
  /** The mode in effect: a brand's clamp when one applies, else the viewer's own choice. */
  mode: ThemeMode;
  /** The viewer's own choice (what is stored), whatever a brand is clamping it to. */
  userMode: ThemeMode;
  theme: ResolvedTheme;
  /** A user toggle: cross-fades and persists. `animate` defaults true. */
  setMode: (mode: ThemeMode, animate?: boolean) => void;
  /**
   * Render in `mode` WITHOUT touching the stored choice, or pass null to go back to the
   * viewer's own. For BrandProvider's clamp to a brand's allowed set: that is the brand's
   * decision, not the viewer's, so it must not overwrite their preference (#757).
   */
  setBrandClamp: (mode: ThemeMode | null) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [userMode, setModeState] = useState<ThemeMode>(readStoredMode);
  const [clamp, setBrandClamp] = useState<ThemeMode | null>(null);
  const mode = clamp ?? userMode;
  const [theme, setTheme] = useState<ResolvedTheme>(() => resolve(mode));

  useEffect(() => {
    setTheme(resolve(mode));
    if (mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setTheme(resolve('system'));
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [mode]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // setMode is only ever called from user-initiated toggles (a brand's clamp goes through
  // `setBrandClamp`, which writes no storage), so this is the one
  // place the cross-fade applies. OS-initiated switches (the matchMedia listener
  // above) swap instantly by design — see docs/04-navigation-ia.md §6.
  function setMode(next: ThemeMode, animate = true) {
    const root = document.documentElement;
    if (animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      root.classList.add('theme-transition');
      window.setTimeout(() => root.classList.remove('theme-transition'), 300);
    }
    setModeState(next);
    localStorage.setItem(STORAGE_KEY, next);
  }

  return <ThemeContext.Provider value={{ mode, userMode, theme, setMode, setBrandClamp }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
